import { z } from "zod";
import { AccountStatus, HexColor, Provider } from "./docs.js";
import { AccountId } from "./ids.js";
import { OAuthPurpose, ReturnToPath } from "./meet.js";
/**
 * Account actions (architecture §5, §9). Views never carry secrets: tokens live only in
 * `accountSecrets/{accountId}`, which no client and no action output can read.
 */
export const AccountView = z.object({
    id: AccountId,
    workspaceId: z.string(),
    provider: Provider,
    address: z.string().email(),
    displayName: z.string().nullable(),
    /** Chosen colour, or null for the automatic one. */
    color: HexColor.nullable(),
    status: AccountStatus,
    statusDetail: z.string().nullable(),
    lastSyncedAt: z.date().nullable(),
    createdAt: z.date(),
    /** OAuth scopes granted to this account (names only, never tokens), e.g. to tell if Meet is on. */
    scopes: z.array(z.string()).default([]),
});
export const AccountsListOutput = z.array(AccountView);
export const AccountsDisconnectInput = z.object({ accountId: AccountId });
export const AccountsDisconnectOutput = z.object({
    accountId: AccountId,
    /** Whether the provider accepted the token revocation (best effort; Graph has none). */
    revoked: z.boolean(),
    deletedDocuments: z.number().int().nonnegative(),
});
/** Sets or clears (null) a mailbox's colour in the inbox. */
export const AccountsSetColorInput = z.object({ accountId: AccountId, color: HexColor.nullable() });
export const AccountsMoveInput = z.object({ accountId: AccountId, workspaceId: z.string().min(1) });
/**
 * `GET /oauth/{provider}/start?workspaceId=…` (plain HTTP, not tRPC): where to navigate next.
 * `purpose=meet&accountId=…` asks an existing Google account for the Meet scopes only; the
 * callback then returns to `returnTo` (a web app path) instead of account settings.
 */
export const OAuthProviderSlug = z.enum(["google", "microsoft"]);
export const OAuthStartInput = z
    .object({
    workspaceId: z.string().min(1),
    purpose: OAuthPurpose.default("connect"),
    accountId: AccountId.optional(),
    returnTo: ReturnToPath.optional(),
})
    .refine((i) => i.purpose === "connect" || i.accountId !== undefined, { message: "purpose=meet needs accountId", path: ["accountId"] });
export const OAuthStartOutput = z.object({ url: z.string().url() });
export const OAuthCallbackInput = z.union([
    z.object({ code: z.string().min(1), state: z.string().min(1) }),
    z.object({ error: z.string().min(1), state: z.string().optional(), error_description: z.string().optional() }),
]);
//# sourceMappingURL=accounts.js.map